"use client"

import type React from "react"

import Link from "next/link"
import { useAuth } from "@/contexts/AuthContext"
import { sendEmailVerification } from "firebase/auth"
import { useEffect, useState } from "react"
import { ChevronDown, LogOut, User } from "lucide-react"
import { useRouter } from "next/navigation"

const Header = () => {
  const { user, loading, isEmailVerified, openAuthModal, logout } = useAuth()
  const [isClient, setIsClient] = useState(false)
  const router = useRouter()

  useEffect(() => {
    setIsClient(true)
  }, [])

  const handleAuthClick = async () => {
    if (user) {
      if (!isEmailVerified) {
        try {
          await sendEmailVerification(user)
          alert("En ny verifieringslänk har skickats till din e-post.")
        } catch (error) {
          console.error("Error sending verification email:", error)
          alert("Det gick inte att skicka verifieringsmeddelandet. Försök igen senare.")
        }
      } else {
        // For logged in users, we'll now navigate to mina-sidor instead of logging out
        // The logout functionality is moved to the dropdown
      }
    } else {
      openAuthModal()
    }
  }

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault()
    try {
      await logout()
      // Always redirect to home page after logout
      router.push("/")
    } catch (error) {
      console.error("Error logging out:", error)
      alert("Fel vid utloggning. Prova igen.")
    }
  }

  const getButtonText = () => {
    if (!isClient || loading) return "Laddar..."
    if (!user) return "Logga in"
    if (!isEmailVerified) return "Verifiera e-post"
    return "Mina sidor" // Changed from "Logga ut" to "Mina sidor"
  }

  return (
    <header className="bg-[#2d4191] text-[#F4f4f4] py-6">
      <nav className="max-w-7xl mx-auto px-2 flex flex-col sm:flex-row justify-between items-center h-full space-y-4 sm:space-y-0">
        <Link href="/" className="text-2xl font-semibold text-[#F4f4f4]">
          Väljratt.se
        </Link>
        <ul className="flex items-center space-x-4 sm:space-x-6 text-center">
          <li>
            <Link
              href="/"
              className="text-sm sm:text-base text-[#F4f4f4] hover:text-blue-300 transition duration-300 font-medium text-lg"
            >
              Hem
            </Link>
          </li>
          <li>
            <Link
              href="/skola"
              className="text-sm sm:text-base text-[#F4f4f4] hover:text-blue-300 transition duration-300 font-medium text-lg"
            >
              Hitta skola
            </Link>
          </li>
          <li className="relative">
            <div className="inline-block relative">
              <Link
                href="/aktivitet"
                className="text-sm sm:text-base text-[#F4f4f4] hover:text-blue-300 transition duration-300 font-medium text-lg"
              >
                Hitta aktivitet
              </Link>
              <span className="absolute top-[-8px] right-[-12px] bg-red-500 text-white text-xs px-1 py-[2px] rounded-md transform rotate-12">
                Beta
              </span>
            </div>
          </li>
          <li>
            <Link
              href="/artiklar"
              className="text-sm sm:text-base text-[#F4f4f4] hover:text-blue-300 transition duration-300 font-medium text-lg"
            >
              Artiklar
            </Link>
          </li>
          <li>
            <Link
              href="/om-oss"
              className="text-sm sm:text-base text-[#F4f4f4] hover:text-blue-300 transition duration-300 font-medium text-lg"
            >
              Om oss
            </Link>
          </li>
          <li className="relative group">
            {user && isEmailVerified ? (
              // For logged in and verified users, show Mina Sidor with dropdown
              <>
                <Link
                  href="/mina-sidor"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition duration-300 text-lg text-sm sm:text-base flex items-center gap-1"
                >
                  <User className="h-4 w-4" />
                  {getButtonText()}
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Link>
                {/* Dropdown menu - width adjusted to match button */}
                <div className="absolute right-0 mt-2 bg-white rounded-md shadow-lg overflow-hidden z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 min-w-full">
                  <div className="py-1">
                    <a
                      href="#"
                      onClick={handleLogout}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center justify-center"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Logga ut
                    </a>
                  </div>
                </div>
              </>
            ) : (
              // For non-logged in users or unverified users, show the regular button
              <button
                onClick={handleAuthClick}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition duration-300 text-lg text-sm sm:text-base"
              >
                {getButtonText()}
              </button>
            )}
          </li>
        </ul>
      </nav>
    </header>
  )
}

export default Header

